import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, Settings } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { getLanguageFromLocale, t } from "@/utils/locale";
import { OnboardingChecklist } from "@/components/settings/OnboardingChecklist";
import { OnboardingWizard } from "@/components/settings/OnboardingWizard";
import { OnboardingTour } from "@/components/OnboardingTour";
import DashboardSetupIncomplete from "@/components/dashboard/DashboardSetupIncomplete";
import DashboardBanners from "@/components/dashboard/DashboardBanners";
import DashboardMenuLink from "@/components/dashboard/DashboardMenuLink";
import DashboardModuleButtons from "@/components/dashboard/DashboardModuleButtons";
import DashboardDailyStats from "@/components/dashboard/DashboardDailyStats";
import DashboardCharts from "@/components/dashboard/DashboardCharts";

export default function DashboardPage() {
  const { authAxios, canSeeFinancials } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveryOpen, setDeliveryOpen] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [activationStatus, setActivationStatus] = useState(null);
  const [dismissedEmptyMenu, setDismissedEmptyMenu] = useState(
    () => sessionStorage.getItem("dismissed_empty_menu") === "1"
  );
  const [dismissedEmailVerify, setDismissedEmailVerify] = useState(
    () => sessionStorage.getItem("dismissed_email_verify") === "1"
  );

  const fetchData = useCallback(async () => {
    try {
      const endpoints = [
        { name: "stats", method: () => authAxiosRef.current.get("/dashboard/stats") },
        { name: "analytics", method: () => authAxiosRef.current.get("/dashboard/analytics?days=14") },
        { name: "restaurant", method: () => authAxiosRef.current.get("/restaurant") },
        { name: "activation", method: () => authAxiosRef.current.get("/activation/checklist") },
        { name: "reviews", method: () => authAxiosRef.current.get("/reviews/stats") },
      ];

      const results = await Promise.all(
        endpoints.map(async (ep) => {
          try {
            const res = await ep.method();
            return { name: ep.name, data: res.data, error: null, status: res.status };
          } catch (err) {
            const status = err.response?.status || "unknown";
            const message = err.response?.statusText || err.message || "unknown error";
            console.error(`[DASHBOARD_LOAD_PARTIAL_FAILURE] endpoint=${ep.name} status=${status} message=${message}`);
            return { name: ep.name, data: null, error: message, status };
          }
        })
      );

      const statsRes = results.find(r => r.name === "stats");
      const analyticsRes = results.find(r => r.name === "analytics");
      const restRes = results.find(r => r.name === "restaurant");
      const activationRes = results.find(r => r.name === "activation");
      const reviewStatsRes = results.find(r => r.name === "reviews");

      setStats(statsRes?.data || null);
      setAnalytics(analyticsRes?.data || null);
      setRestaurant(restRes?.data || null);
      setReviewStats(reviewStatsRes?.data || null);
      setActivationStatus(activationRes?.data || null);

      const failedEndpoints = results.filter(r => r.error);
      if (failedEndpoints.length > 0) {
        if (restRes?.error) {
          toast.error("Error cargando datos del restaurante");
        } else {
          toast.error("Error cargando algunos datos del dashboard");
        }
      }
    } catch {
      toast.error("Error cargando datos");
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);


  const isSetupComplete = activationStatus
    ? (activationStatus.ready_to_operate && activationStatus.onboarding_completed)
    : (restaurant?.onboarding_completed || restaurant?.is_active);

  useEffect(() => {
    if (loading) return;
    if (!isSetupComplete && !sessionStorage.getItem("wizard_dismissed")) setShowWizard(true);
  }, [loading, isSetupComplete]);

  const menuSlug = stats?.restaurant_slug || restaurant?.slug || stats?.restaurant_id;
  const hasCustomDomain = stats?.custom_domain && stats?.domain_status === "active";
  const menuUrl = hasCustomDomain
    ? `https://${stats.custom_domain}`
    : (menuSlug && menuSlug !== "undefined" && menuSlug !== "null") ? `https://${menuSlug}.kaisosystem.com` : "";
  const copyLink = () => { navigator.clipboard.writeText(menuUrl); toast.success("Link copiado"); };

  if (loading) return <LoadingScreen />;

  if (!isSetupComplete) {
    return (
      <MainLayout>
        <DashboardSetupIncomplete
          restaurant={restaurant} stats={stats} activationStatus={activationStatus}
          showWizard={showWizard} setShowWizard={setShowWizard}
          fetchData={fetchData} authAxios={authAxios} navigate={navigate}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <OnboardingTour lang={getLanguageFromLocale()} isRestaurantActivated={isSetupComplete} />
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4 bg-card border border-border/50 rounded-xl p-4" data-testid="dashboard-header">
          <h1 className="text-2xl font-bold uppercase tracking-tight" data-testid="restaurant-name">{stats?.restaurant_name || "Mi Restaurante"}</h1>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${deliveryOpen ? "bg-green-500" : "bg-red-500"}`} />
              <span className={`text-sm font-semibold ${deliveryOpen ? "text-green-600" : "text-red-500"}`}>
                {deliveryOpen ? t("deliveryOpen") : t("deliveryClosed")}
              </span>
              <Switch checked={deliveryOpen} onCheckedChange={setDeliveryOpen} data-testid="delivery-toggle" />
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/settings")} data-testid="config-btn">
              <Settings className="w-4 h-4" />{t("configure")}
            </Button>
          </div>
        </div>

        <DashboardBanners
          stats={stats} restaurant={restaurant} isSetupComplete={isSetupComplete}
          dismissedEmptyMenu={dismissedEmptyMenu} setDismissedEmptyMenu={setDismissedEmptyMenu}
          dismissedEmailVerify={dismissedEmailVerify} setDismissedEmailVerify={setDismissedEmailVerify}
          navigate={navigate} authAxios={authAxios}
        />

        <OnboardingChecklist
          restaurant={restaurant} productCount={stats?.total_products || 0}
          categoryCount={stats?.total_categories || 0} onStartWizard={() => setShowWizard(true)}
          onActivated={fetchData} activationStatus={activationStatus}
        />
        {showWizard && (
          <OnboardingWizard
            key="wizard-main-dashboard" open={true}
            onClose={() => { setShowWizard(false); sessionStorage.setItem("wizard_dismissed", "1"); fetchData(); }}
            authAxios={authAxios} restaurant={restaurant}
            onComplete={() => { fetchData(); navigate("/menu"); }}
          />
        )}

        <DashboardMenuLink
          menuUrl={menuUrl} menuSlug={menuSlug} restaurant={restaurant}
          showQR={showQR} setShowQR={setShowQR} copyLink={copyLink} navigate={navigate}
        />
        <DashboardModuleButtons navigate={navigate} />
        <DashboardDailyStats
          stats={stats} analytics={analytics}
          reviewStats={reviewStats} canSeeFinancials={canSeeFinancials}
        />
        <DashboardCharts analytics={analytics} canSeeFinancials={canSeeFinancials} />

        {stats?.low_stock_alerts > 0 && (
          <Card className="border-yellow-400 bg-yellow-50">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              <p className="font-medium">Tienes {stats.low_stock_alerts} productos con stock bajo</p>
              <Button variant="outline" size="sm" className="ml-auto" onClick={() => navigate("/stock")}>Ver inventario</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
